(() => {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const body = document.body;
  const overlay = qs('[data-mobile-overlay]');
  const toast = qs('[data-toast]');
  let toastTimer;

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD' }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  };

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
  };

  const closeDrawers = () => {
    qs('[data-mobile-menu]')?.classList.remove('is-open');
    qs('[data-filter-drawer]')?.classList.remove('is-open');
    overlay?.classList.remove('is-visible');
    body.classList.remove('menu-open', 'filter-open');
    qs('[data-mobile-menu]')?.setAttribute('aria-hidden', 'true');
    qs('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
  };

  const mobileMenu = qs('[data-mobile-menu]');
  qs('[data-menu-toggle]')?.addEventListener('click', () => {
    const opening = !mobileMenu?.classList.contains('is-open');
    closeDrawers();
    if (opening && mobileMenu) {
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      overlay?.classList.add('is-visible');
      body.classList.add('menu-open');
      qs('[data-menu-toggle]')?.setAttribute('aria-expanded', 'true');
    }
  });
  qs('[data-menu-close]')?.addEventListener('click', closeDrawers);
  overlay?.addEventListener('click', closeDrawers);
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeDrawers(); });

  const headerSearch = qs('.header-search');
  qs('[data-search-toggle]')?.addEventListener('click', () => {
    headerSearch?.classList.toggle('is-open');
    if (headerSearch?.classList.contains('is-open')) qs('[data-predictive-input]', headerSearch)?.focus();
  });

  qsa('[data-predictive-search]').forEach((container) => {
    const input = qs('[data-predictive-input]', container);
    const results = qs('[data-predictive-results]', container);
    if (!input || !results) return;
    let timer;
    let controller;

    const clear = () => { results.hidden = true; results.innerHTML = ''; };
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) { clear(); return; }
      timer = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        try {
          const url = `${window.routes.predictive_search_url}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Search request failed');
          const data = await response.json();
          const products = data?.resources?.results?.products || [];
          if (!products.length) {
            results.innerHTML = '<div class="predictive-results__footer">No matching fragrances found</div>';
          } else {
            results.innerHTML = products.map((product) => `
              <a class="predictive-results__item" href="${escapeHtml(product.url)}">
                ${product.image ? `<img src="${escapeHtml(product.image)}" width="62" height="74" alt="">` : '<span></span>'}
                <span><small>${escapeHtml(product.vendor || '')}</small><strong>${escapeHtml(product.title)}</strong></span>
                <strong>${escapeHtml(product.price || '')}</strong>
              </a>`).join('') + `<a class="predictive-results__footer" href="/search?q=${encodeURIComponent(query)}&type=product">View all results →</a>`;
          }
          results.hidden = false;
        } catch (error) {
          if (error.name !== 'AbortError') clear();
        }
      }, 220);
    });
    input.addEventListener('keydown', (event) => { if (event.key === 'Escape') clear(); });
    document.addEventListener('click', (event) => { if (!container.contains(event.target)) clear(); });
  });

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      qsa('.cart-count').forEach((count) => {
        count.textContent = cart.item_count;
        count.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (error) { /* Cart count refresh is non-critical. */ }
  };

  qsa('.quick-add-form').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = qs('button[type="submit"]', form);
      const original = button?.textContent;
      if (button) { button.disabled = true; button.textContent = 'Adding…'; }
      try {
        const response = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Unable to add product');
        await updateCartCount();
        showToast(`${data.product_title || 'Fragrance'} added to cart`);
      } catch (error) {
        showToast(error.message || 'Unable to add product');
      } finally {
        if (button) { button.disabled = false; button.textContent = original; }
      }
    });
  });

  const wishlistKey = 'fragrance-market-wishlist';
  let wishlist = [];
  try { wishlist = JSON.parse(localStorage.getItem(wishlistKey) || '[]'); } catch (error) { wishlist = []; }
  qsa('[data-product-card]').forEach((card) => {
    const handle = card.dataset.productHandle;
    const button = qs('[data-wishlist-button]', card);
    if (!handle || !button) return;
    button.classList.toggle('is-active', wishlist.includes(handle));
    button.addEventListener('click', () => {
      wishlist = wishlist.includes(handle) ? wishlist.filter((item) => item !== handle) : [...wishlist, handle];
      localStorage.setItem(wishlistKey, JSON.stringify(wishlist));
      button.classList.toggle('is-active', wishlist.includes(handle));
      showToast(wishlist.includes(handle) ? 'Saved to your wishlist' : 'Removed from your wishlist');
    });
  });

  qsa('[data-carousel-section]').forEach((section) => {
    const track = qs('[data-carousel-track]', section);
    if (!track) return;
    const scroll = (direction) => track.scrollBy({ left: direction * track.clientWidth * 0.82, behavior: 'smooth' });
    qs('[data-carousel-prev]', section)?.addEventListener('click', () => scroll(-1));
    qs('[data-carousel-next]', section)?.addEventListener('click', () => scroll(1));
  });

  qsa('[data-countdown]').forEach((timer) => {
    const hours = Math.max(1, Number(timer.dataset.countdown || 24));
    const storageKey = `deal-countdown-${hours}`;
    let end = Number(sessionStorage.getItem(storageKey));
    if (!end || end < Date.now()) {
      end = Date.now() + hours * 60 * 60 * 1000;
      sessionStorage.setItem(storageKey, String(end));
    }
    const target = qs('[data-countdown-value]', timer);
    const update = () => {
      const remaining = Math.max(0, end - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
      const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
      const s = String(totalSeconds % 60).padStart(2, '0');
      if (target) target.textContent = `${h}:${m}:${s}`;
    };
    update(); window.setInterval(update, 1000);
  });

  const filterDrawer = qs('[data-filter-drawer]');
  qs('[data-filter-toggle]')?.addEventListener('click', () => {
    filterDrawer?.classList.add('is-open'); overlay?.classList.add('is-visible'); body.classList.add('filter-open');
  });
  qs('[data-filter-close]')?.addEventListener('click', closeDrawers);
  qs('[data-sort-select]')?.addEventListener('change', (event) => event.target.form?.submit());

  const productRoot = qs('[data-product-root]');
  if (productRoot) {
    qsa('[data-gallery-thumb]', productRoot).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = thumb.dataset.galleryThumb;
        qsa('[data-gallery-thumb]', productRoot).forEach((item) => item.classList.toggle('is-active', item === thumb));
        qsa('[data-gallery-media]', productRoot).forEach((media) => media.classList.toggle('is-active', media.dataset.galleryMedia === index));
      });
    });

    const quantityInput = qs('[data-qty-input]', productRoot);
    qs('[data-qty-minus]', productRoot)?.addEventListener('click', () => { if (quantityInput) quantityInput.value = Math.max(1, Number(quantityInput.value || 1) - 1); });
    qs('[data-qty-plus]', productRoot)?.addEventListener('click', () => { if (quantityInput) quantityInput.value = Math.max(1, Number(quantityInput.value || 1) + 1); });

    const variantsScript = qs('[data-product-variants]', productRoot);
    const select = qs('[data-variant-select]', productRoot);
    let variants = [];
    try { variants = JSON.parse(variantsScript?.textContent || '[]'); } catch (error) { variants = []; }
    select?.addEventListener('change', () => {
      const variant = variants.find((item) => String(item.id) === String(select.value));
      if (!variant) return;
      const priceWrapper = qs('[data-price-wrapper]', productRoot);
      const currentPrice = qs('[data-product-price]', priceWrapper);
      const comparePrice = qs('[data-compare-price]', priceWrapper);
      const availability = qs('[data-product-availability]', productRoot);
      const addButton = qs('[data-add-to-cart]', productRoot);
      const sku = qs('[data-product-sku]', productRoot);
      if (currentPrice) currentPrice.textContent = formatMoney(variant.price);
      if (comparePrice) {
        comparePrice.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
        comparePrice.hidden = !(variant.compare_at_price > variant.price);
      }
      if (availability) {
        availability.classList.toggle('is-unavailable', !variant.available);
        availability.lastChild.textContent = variant.available ? 'In stock and ready to ship' : 'Currently unavailable';
      }
      if (addButton) { addButton.disabled = !variant.available; addButton.textContent = variant.available ? 'Add to cart' : 'Sold out'; }
      if (sku) sku.textContent = variant.sku || '—';
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url);
    });
  }

  qsa('[data-recommendations-url]').forEach(async (section) => {
    if (section.innerHTML.trim()) return;
    try {
      const response = await fetch(section.dataset.recommendationsUrl);
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = qs('[data-recommendations-url]', documentFragment);
      if (replacement?.innerHTML.trim()) section.innerHTML = replacement.innerHTML;
    } catch (error) { /* Recommendations are optional. */ }
  });
})();
